import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import MobileListCard from '../components/MobileListCard';
import EmptyState from '../components/EmptyState';
import moment from 'moment';
import StatusBadge from '../components/StatusBadge';

const AssignmentScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/assignments/get-assignment-list-student?PageSize=10&PageNumber=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = response.data.data; 

      if (page === 1) {
        setItems(data);
      } else {
        setItems((prevItems) => [...prevItems, ...data]);
      }

      setHasMore(data.length === 10);
      setPageNumber(page + 1);
    } catch (error) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, []);

  const onRefresh = useCallback(async () => {
    setHasMore(true);
    setPageNumber(1);
    await loadData(1);
  }, []);

  const onView = (item) => {
    navigate('/assignment-submit', { state: { item } });
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadData(pageNumber);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Assignments"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<AssignmentIcon />}
            iconColor="#43A047"
            title="No Assignments"
            subtitle="No assignments have been posted yet."
            actionLabel="Retry"
            onAction={() => loadData(1)}
          />
        ) : (
          <Box
            sx={{
              backgroundColor: '#fff',
              mt: 1,
              mx: 1,
              borderRadius: '12px 12px 0 0',
              overflow: 'hidden',
            }}
          >
            {items.map((item, index) => (
              <MobileListCard
                key={item.id}
                icon={<AssignmentIcon />}
                iconColor="#43A047"
                iconBg="rgba(67,160,71,0.1)"
                title={item.title}
                subtitle={`${item.subjectName || ''}  •  Due: ${moment(item.toBeSubmittedOn).format('DD MMM YYYY')}`}
                description={`By ${item.teacherName || ''}`}
                rightIcon={
                  <StatusBadge
                    status={
                      moment().isAfter(moment(item.toBeSubmittedOn), 'day')
                        ? 'overdue'
                        : 'pending'
                    }
                  />
                }
                onClick={() => onView(item)}
                showDivider={index < items.length - 1}
                showChevron
              />
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box
                    onClick={loadMore}
                    sx={{
                      fontSize: 14,
                      color: colors.BLUE,
                      cursor: 'pointer',
                      fontWeight: 500,
                      py: 1,
                    }}
                  >
                    Load more
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssignmentScreen;
